import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type TAdminPageLayoutProps = {
  title: string;
  subtitle?: string;
  /** Action buttons displayed on the right side of the header */
  actions?: ReactNode;
  children: ReactNode;
  maxWidth?: "4xl" | "7xl";
  bgColor?: "white" | "gray";
  className?: string;
};

/**
 * Shared layout shell for admin pages.
 * Provides consistent header (title + subtitle + actions) and content area.
 */
export function AdminPageLayout({
  title,
  subtitle,
  actions,
  children,
  maxWidth = "7xl",
  bgColor = "gray",
  className,
}: TAdminPageLayoutProps) {
  const maxWidthClass = maxWidth === "4xl" ? "max-w-4xl" : "max-w-7xl";
  const bgClass = bgColor === "white" ? "bg-background" : "bg-muted";

  return (
    <div className={cn("h-full overflow-y-auto p-4 sm:p-6", bgClass, className)}>
      <div className={cn("mx-auto space-y-6", maxWidthClass)}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">{title}</h1>
            {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}
